import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { tactile } from "@/shared/telegram";

import { Icon } from "./Icon";

/**
 * Копирование в буфер. `navigator.clipboard` в Telegram WebView есть не
 * всегда (нет на части Android-клиентов и вне https), поэтому отказ не
 * замалчивается: подпись остаётся прежней, и адрес рядом виден целиком и
 * доступен для выделения руками.
 *
 * Движение (FE-13): подписи не подменяются, а разъезжаются по вертикали.
 * Обе лежат в одной ячейке грида, поэтому ширина кнопки равна большей из них
 * и не прыгает в момент смены.
 */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={() => {
        // Нажатие отзывается делегатом по утилите `press`; здесь остаётся
        // только исход — он приходит асинхронно и к касанию не привязан.
        void navigator.clipboard?.writeText(value).then(
          () => {
            setCopied(true);
            tactile("success");
          },
          () => setCopied(false),
        );
      }}
      className="press relative grid shrink-0 overflow-hidden rounded-xl bg-surface-secondary px-3 py-2 text-caption font-semibold text-text-primary"
    >
      <span
        className={[
          "col-start-1 row-start-1 flex items-center justify-center transition duration-[var(--motion-base)] ease-out-quint",
          copied ? "-translate-y-2 opacity-0" : "translate-y-0 opacity-100",
        ].join(" ")}
      >
        {label ?? t("common.copy")}
      </span>

      <span
        aria-hidden={!copied}
        className={[
          "col-start-1 row-start-1 flex items-center justify-center gap-1 transition duration-[var(--motion-base)] ease-out-quint",
          copied ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0",
        ].join(" ")}
      >
        <Icon name="check" size={12} className="text-action-primary" />
        {t("common.copied")}
      </span>
    </button>
  );
}
